import React, { Fragment } from 'react';
import { fetchNavHTML } from 'libs/server/nav';
import { fetchUser } from 'libs/server/users';
import { GetServerSideProps } from 'next';

import { PrimaryNavIdentifier } from '@zola-helpers/client/dist/es/constants/navConstants';
import { UserContext } from '@zola-helpers/client/dist/es/@types';

import Masthead, { MastheadProps } from '@zola/zola-ui/src/components/PreAuthV3/Masthead';

import { AppDownloadBannerMobile } from '@zola/zola-ui/src/components/AppDownloadBannerMobile/AppDownloadBannerMobile';
import {
  Differentiation,
  CategoryTiles,
  ProductGrid,
} from '@zola/zola-ui/src/components/PreAuthV3';
import SupportAccordion from '@zola/zola-ui/src/components/PreAuthV3/SupportAccordion';

import { WCardSuiteSearchView, WRelatedLinkView } from '@zola/svc-web-api-ts-client';

import Nav from 'components/Nav';

import { getFeaturedInvitations, getFeaturedSaveTheDates } from 'libs/server/invitesAndPaper';
import GridTextItem from 'components/LP/Paper/v3/components/GridTextItem';
import { reviewItems } from 'components/LP/Paper/v3/content/reviews';
import { PaperInvitesPageWrapper } from 'components/LP/v3/PaperInvitesPageWrapper.styles';
import { SecondaryNavType } from 'components/Nav/types';
import { useIsPostAuthNav3MobileOrDesktop } from 'hooks/useIsPostAuthNav3Active';

import { differentiationContent } from 'components/LP/Paper/v3/content/differentiationAlt';
import { shopByCategoryTiles } from 'components/LP/Paper/v3/content/shopByCategoryTilesAlt';
import { shopByStyleTiles } from 'components/LP/Paper/v3/content/shopByStyleTilesAlt';
import { FAQItems } from 'components/LP/Paper/v3/content/faq';
import CardPreview from 'components/LP/Paper/v3/components/CardPreview';
import NextBackgroundForegroundImages from 'components/PreauthV3/NextBackgroundForegroundImages';
import FeaturedArticlesCarousel from 'components/expert-advice/FeaturedArticlesCarousel';

import Header from 'components/LP/Paper/components/PaperLPHeader';
import { fetchSEORelatedLinks } from 'libs/server/seo';
import { getIsGuest } from 'libs/universal/utils/getIsGuest';
import { PostType } from 'libs/contentful/types';
import { getCachedPostData } from 'libs/contentful/api';
import { excludeMobile, mobileOnly } from '@zola/zola-ui/src/styles/emotion/mixins/responsive';
import {
  experimentFlagsSSR,
  NEW_PAPER_TYPE_NAPKINS,
  NEW_PAPER_TYPE_SIGNAGE,
  NEW_PAPER_TYPE_STICKERS,
} from 'libs/client/utils/experimentFlags';
import { NickViallMasthead } from 'components/LP/Paper/v3/components/NickViallMasthead';
import { stripUndef } from 'libs/universal/utils/type';
import Footer from 'components/ui/Footer/Footer';

const HeroBackgroundDesktopWebp =
  '/static-preauth/images/PaperLP/v3/Masthead/Desktop/HeroBackground.webp';

const exploreDesignsText = 'Explore designs';
const GridTextItem1 = (
  <GridTextItem
    hed={
      <Fragment>
        Save the dates worth <span> saving</span>
      </Fragment>
    }
    cta={{ text: exploreDesignsText, href: '/wedding-planning/save-the-date/shop' }}
  />
);
const GridTextItem2 = (
  <GridTextItem
    hed={
      <Fragment>
        Invites &amp; RSVPs for<span> every</span> wedding style
      </Fragment>
    }
    cta={{ text: exploreDesignsText, href: '/wedding-planning/invitations/shop' }}
  />
);
const GridTextItem3 = (
  <GridTextItem
    hed={
      <Fragment>
        <span>Matching</span> suites and day-of paper
      </Fragment>
    }
    cta={{ text: exploreDesignsText, href: '/wedding-planning/suites/shop' }}
  />
);
const GridTextItem4 = (
  <GridTextItem
    hed={
      <Fragment>
        Premium quality, <span> perfectly priced</span>
      </Fragment>
    }
    cta={{ text: exploreDesignsText, href: '/wedding-planning/suites/shop' }}
  />
);

const getMastheadProps = (): Partial<MastheadProps> => {
  return {
    altMedia: (
      <NextBackgroundForegroundImages
        backgroundImg={{
          src: HeroBackgroundDesktopWebp,
          alt: 'Flat lay of invitations and envelopes with a bouquet of flowers',
        }}
        priority
      />
    ),
  };
};

type PaperLPContainerProps = {
  navHTML: string | null;
  userCtx: UserContext | null;
  saveTheDates: null | WCardSuiteSearchView;
  invitations: null | WCardSuiteSearchView;
  adviceLinks: WRelatedLinkView[];
  articles: PostType[];
  enabledCategoryExperiments: string[];
};
const PaperLPContainer: React.FC<PaperLPContainerProps> = (props): JSX.Element => {
  const {
    navHTML,
    userCtx,
    saveTheDates,
    invitations,
    adviceLinks,
    articles,
    enabledCategoryExperiments,
  } = props;

  const isPostAuthNav3Enabled = useIsPostAuthNav3MobileOrDesktop(userCtx);

  const { suites: saveTheDateSuites } = saveTheDates || {};
  const { suites: invitationSuites } = invitations || {};

  const saveTheDateCards =
    saveTheDateSuites?.map(suite => <CardPreview card={suite} nextImage />) || [];

  const invitationCards =
    invitationSuites?.map(suite => <CardPreview card={suite} nextImage />) || [];

  const cards = [
    GridTextItem1,
    ...saveTheDateCards,
    GridTextItem2,
    GridTextItem3,
    ...invitationCards,
    GridTextItem4,
  ];
  const mobileStdTiles = saveTheDateCards?.slice(0, 4) || [];
  const mobileInvitationTiles = invitationCards?.slice(0, 4) || [];
  const mobileCards = [
    GridTextItem1,
    ...mobileStdTiles,
    GridTextItem2,
    GridTextItem3,
    ...mobileInvitationTiles,
    GridTextItem4,
  ];

  const getShopByCategoryTiles = () =>
    shopByCategoryTiles.filter(data => {
      if (!data.experimentFlag) return true;
      return enabledCategoryExperiments.includes(data.experimentFlag);
    });

  return (
    <>
      <Header />
      {getIsGuest(userCtx) ? <AppDownloadBannerMobile campaignName="zola_web_lp_preauth" /> : <></>}
      <Nav
        navHTML={navHTML}
        showPromo
        user={userCtx}
        disablePrimaryNavCollapse
        secondaryNavType={SecondaryNavType.PAPER}
        {...(isPostAuthNav3Enabled && { activePrimaryLink: PrimaryNavIdentifier.PAPER })}
      />
      <PaperInvitesPageWrapper isPostAuthNav3={isPostAuthNav3Enabled}>
        <Masthead
          btnHref="/wedding-planning/save-the-date/shop"
          btnText="Explore save the dates"
          secondaryBtnHref="/wedding-planning/invitations/shop"
          secondaryBtnText="Explore invites"
          isPhotoOnRight
          title={
            <h1>
              All the <span>wedding invitations</span>, from save the date to thanks so much
            </h1>
          }
          valueProps={[
            'Premium quality without the premium price',
            'Highly-customizable designs for every wedding moment',
            'Add a QR code for digital wedding RSVPs and registry shopping',
          ]}
          {...getMastheadProps()}
        />
        <CategoryTiles
          title="Shop by wedding paper category"
          categories={getShopByCategoryTiles()}
          square
        />
        <div css={excludeMobile}>
          <ProductGrid gridItems={cards} />
        </div>
        <div css={mobileOnly}>
          <ProductGrid gridItems={mobileCards} />
        </div>
        <NickViallMasthead />
        <Differentiation
          content={differentiationContent}
          title={{
            titleStart: 'All the paper, all the',
            highlightText: 'perks',
          }}
          isPhotoOnRight={false}
        />
        <CategoryTiles
          title="Shop our invitation &amp; save the date styles"
          categories={shopByStyleTiles}
          square
        />
        <FeaturedArticlesCarousel articles={articles} />
        <div>
          <SupportAccordion
            advice={{ links: adviceLinks ?? undefined }}
            faqs={FAQItems}
            reviews={reviewItems}
            openSections={['faqs']}
          />
        </div>
        <Footer userCtx={userCtx} />
      </PaperInvitesPageWrapper>
    </>
  );
};

const CATEGORY_EXPERIMENTS = [
  NEW_PAPER_TYPE_NAPKINS,
  NEW_PAPER_TYPE_SIGNAGE,
  NEW_PAPER_TYPE_STICKERS,
];

export const getServerSideProps: GetServerSideProps<PaperLPContainerProps> = async ({ req }) => {
  const [navHTML, userCtx, saveTheDates, invitations, adviceLinks] = await Promise.all([
    fetchNavHTML(req),
    fetchUser(req),
    getFeaturedSaveTheDates(),
    getFeaturedInvitations(),
    fetchSEORelatedLinks({
      topicSlug: 'invites-paper',
      subtopicSlug: 'invitations',
    }),
  ]);

  const articles = await Promise.all([
    getCachedPostData('wedding-invitations-paper-guide', req),
    getCachedPostData('whats-included-in-a-wedding-invitation-suite', req),
  ]).catch(() => []);

  const enabledCategoryExperiments = userCtx
    ? CATEGORY_EXPERIMENTS.filter(experiment => {
        const variation = experimentFlagsSSR.getExperimentVariation(
          experiment,
          userCtx,
          req.cookies
        );
        return !!variation && variation !== 'NOT_IN_TEST';
      })
    : [];

  return {
    props: {
      navHTML,
      userCtx,
      saveTheDates: saveTheDates ?? null,
      invitations: invitations ?? null,
      adviceLinks,
      enabledCategoryExperiments,
      ...stripUndef({ articles: articles.map(({ page }) => page) }),
    },
  };
};
export default PaperLPContainer;
